import { SOCIAL_EXTERNAL_IDENTITY_PROVIDER_APPLE } from '@eventbrite/social-login-facebook';
import { ISocialLogInApiResponseProps, socialLogIn } from '../api/social';
import {
    GA_GOOGLE_ONE_TAP_CLICKED,
    TOSMODAL_SOCIAL_SHOWED,
} from '../constants/analytics';
import {
    LOOKUP_SOCIAL_USER_EXISTENT_IN_SOFT_ACCOUNT,
    LOOKUP_SOCIAL_USER_UNEXISTENT,
} from '../constants/apiErrors';
import { IAllContextsProps } from '../constants/interfaces';
import { trackLoginSuccessful } from '../tracking/login';
import {
    trackAccountAutolinked,
    trackSocialAction,
    trackSocialLogInSuccess,
    trackSocialProviderClick,
} from '../tracking/social';
import { parseFormPostData } from '../utils';
import { onSkipSocialLinks } from '../utils/mocks';
import { getNewUserRedirectUrl } from '../utils/signup';
import { getSocialLogInFormData } from '../utils/social';
import { handleAppleLoginAttempt } from './appleHandlers';
import { lookUpUserAction } from './lookup';
import { handleSocialLogInErrors } from './socialErrors';
import { handleShowToS } from './tos';

export interface ISocialProviderClickActionProps {
    socialProvider: string;
    stateFromProvider: any;
    isOneTap?: boolean;
    contexts: IAllContextsProps;
}

export interface ISocialLogInUserActionDataProps {
    accessToken: string;
    email: string;
    firstName: string;
    lastName: string;
    picture: string;
    skipCreation: boolean;
    sourceProvider: string;
    userID: string;
    sub?: string;
}

export interface ISocialDataProps {
    [x: string]: ISocialLogInUserActionDataProps;
}

export const socialProviderClickAction = ({
    socialProvider,
    stateFromProvider,
    isOneTap = false,
    contexts,
}: ISocialProviderClickActionProps) => {
    contexts.userContext?.methods.setTosSocialAccepted(false);

    trackSocialProviderClick({
        socialProvider,
        stateFromProvider,
        analytics: contexts.configContext?.analytics,
    });

    if (isOneTap) {
        trackSocialAction({
            socialProvider,
            action: GA_GOOGLE_ONE_TAP_CLICKED,
            analytics: contexts.configContext?.analytics,
        });
    }

    contexts.userContext?.methods.setSocialProvider(socialProvider);

    if (contexts?.configContext?.social?.skipSocialCalls) {
        onSkipSocialLinks(socialProvider, contexts);
    }
};

/**
 * Tries to log in an user coming from a social provider.
 * For apple we have added an intermediate step to let the
 * user add a different account to link if we could not find
 * a matching one with the social email.
 * For facebook and google the login is handled by the api direclty.
 */
// Check this one, it should be executed after the socialLoginClick
export const socialLogInUserAction = ({
    data,
    socialProvider,
    contexts,
}: {
    data: ISocialLogInUserActionDataProps;
    socialProvider: string;
    contexts: IAllContextsProps;
}) => {
    contexts.userContext?.methods.setSocialProvider(socialProvider);

    contexts.configContext?.socialMethods?.attempt?.();

    if (socialProvider === SOCIAL_EXTERNAL_IDENTITY_PROVIDER_APPLE) {
        handleAppleLoginAttempt({ data, socialProvider, contexts });

        // prevents continuing with the lookup action
        return;
    } else {
        contexts.userContext?.methods.setSocialData({ [socialProvider]: data });
    }

    lookUpUserAction({
        data: {
            email: data.email,
            sub: data.userID,
        },
        socialProvider,
        contexts,
    })
        .then((response: any) => {
            if (response.error) {
                return Promise.reject(response.error);
            } else {
                handleSocialLogIn({ data, socialProvider, contexts });
            }
        })
        .catch((error: any) => {
            const message = error?.message;
            if (
                message !== LOOKUP_SOCIAL_USER_EXISTENT_IN_SOFT_ACCOUNT &&
                message !== LOOKUP_SOCIAL_USER_UNEXISTENT
            ) {
                handleSocialLogIn({ data, socialProvider, contexts });
            } else {
                handleShowToS({ action: TOSMODAL_SOCIAL_SHOWED, contexts });
            }
        });
};

// TODO: Is Apple not going through here? old metrics don't take this scenario as possible
export const handleSocialLogIn = ({
    data,
    socialProvider,
    contexts,
}: {
    data: ISocialLogInUserActionDataProps;
    socialProvider?: string;
    contexts: IAllContextsProps;
}) => {
    const _socialProvider = socialProvider || data?.sourceProvider;

    return (
        socialLogIn(
            parseFormPostData(
                getSocialLogInFormData({ formData: data, contexts }),
            ),
        )
            .then((response: ISocialLogInApiResponseProps) => {
                if (response.isAccountAutolinked) {
                    trackAccountAutolinked({
                        socialProvider: _socialProvider,
                        analytics: contexts.configContext?.analytics,
                    });
                    trackLoginSuccessful({
                        socialProvider: _socialProvider,
                        analytics: contexts.configContext?.analytics,
                    });
                } else {
                    trackSocialLogInSuccess({
                        socialProvider: _socialProvider,
                        response,
                        analytics: contexts.configContext?.analytics,
                    });
                }

                handleLoginSuccess({ response, contexts });
            })
            // User may be found in the lookup call or have failed
            .catch((error: any) => {
                contexts.configContext?.socialMethods?.error?.default?.(error);

                handleSocialLogInErrors({
                    _errorSpec: error,
                    socialProvider: _socialProvider,
                    responseErrorObj: data,
                    contexts,
                });
            })
    );
};

export const handleLoginSuccess = ({
    response,
    contexts,
}: {
    response: ISocialLogInApiResponseProps;
    contexts: IAllContextsProps;
}) => {
    if (response.newUser) {
        response.redirectUrl = getNewUserRedirectUrl({
            redirectUrl: response.redirectUrl,
            configContext: contexts.configContext,
        });
    }

    contexts.configContext?.socialMethods?.success?.(response);
};
